/**
 * Distance Service
 * Primary: Google Distance Matrix / Directions API
 * Fallback: Haversine formula
 *
 * Environment variable: GOOGLE_MAPS_API_KEY
 * (No API key fallback to Haversine)
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export interface GeocodeResult {
  coordinates: LatLng;
  formattedAddress?: string; // The address that Google Maps actually found/used
}

export interface DistanceAndDuration {
  distanceKm: number;
  durationMinutes: number;
  method?: string; // 'Google Distance Matrix' | 'Haversine (fallback)'
}

/**
 * In-memory caches
 */
const coordinateCache = new Map<string, LatLng>();
const distanceCache = new Map<string, DistanceAndDuration>();

/**
 * Get Google Maps API key from environment
 */
function getGoogleMapsApiKey(): string | null {
  // Note: In a Vite environment, this would be import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  // For server-side or Node.js, use process.env.GOOGLE_MAPS_API_KEY
  const key =
    (import.meta.env as any).VITE_GOOGLE_MAPS_API_KEY ||
    (typeof process !== "undefined" && process.env?.GOOGLE_MAPS_API_KEY);
  return key ? String(key).trim() : null;
}

/**
 * Check if Google Maps is available
 */
function isGoogleMapsAvailable(): boolean {
  return !!getGoogleMapsApiKey();
}

/**
 * Haversine formula for distance calculation
 */
function haversineDistance(origin: LatLng, destination: LatLng): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((destination.lat - origin.lat) * Math.PI) / 180;
  const dLon = ((destination.lng - origin.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((origin.lat * Math.PI) / 180) *
      Math.cos((destination.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Estimate duration based on distance (Haversine fallback)
 * Assumes 80 km/h average speed
 */
function estimateDurationFromDistance(distanceKm: number): number {
  const avgSpeedKmh = 80;
  return Math.round((distanceKm / avgSpeedKmh) * 60);
}

/**
 * Geocode a location string using Google Geocoding API
 * Returns both coordinates and the formatted address that Google Maps found
 */
async function geocodeWithGoogle(location: string): Promise<GeocodeResult | null> {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return null;
  }

  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      location,
    )}&key=${apiKey}`;

    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`[DISTANCE] Google Geocoding API error: ${response.status}`);
      return null;
    }

    const data = await response.json();

    if (data.status === "OK" && data.results && data.results.length > 0) {
      const result = data.results[0];
      const coords: LatLng = {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng,
      };
      const formattedAddress = result.formatted_address || location; // Use Google's formatted address
      console.log(
        `[DISTANCE] Google Geocoding: "${location}" → (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}) [${formattedAddress}]`,
      );
      coordinateCache.set(location.toLowerCase(), coords);
      return {
        coordinates: coords,
        formattedAddress: formattedAddress,
      };
    }

    console.warn(`[DISTANCE] Google Geocoding: No results for "${location}"`);
    return null;
  } catch (error) {
    console.error("[DISTANCE] Google Geocoding error:", error);
    return null;
  }
}

/**
 * Get coordinates for a location string
 * Uses cache, Google Geocoding API, or returns null
 */
export async function getCoordinatesForLocation(
  location: string,
): Promise<LatLng | null> {
  if (!location || !location.trim()) {
    return null;
  }

  const key = location.toLowerCase();

  // Check cache
  const cached = coordinateCache.get(key);
  if (cached) {
    return cached;
  }

  // Try Google Geocoding
  if (isGoogleMapsAvailable()) {
    const result = await geocodeWithGoogle(location);
    if (result) {
      coordinateCache.set(key, result.coordinates);
      return result.coordinates;
    }
  }

  // No result
  console.warn(`[DISTANCE] Failed to resolve location: "${location}"`);
  return null;
}

/**
 * Get coordinates and formatted address for a location string
 * Returns both the coordinates and the address that Google Maps actually found
 */
export async function getCoordinatesAndAddressForLocation(
  location: string,
): Promise<GeocodeResult | null> {
  if (!location || !location.trim()) {
    return null;
  }

  // Try Google Geocoding (always fetch to get formatted address)
  if (isGoogleMapsAvailable()) {
    const result = await geocodeWithGoogle(location);
    if (result) {
      const key = location.toLowerCase();
      coordinateCache.set(key, result.coordinates); // Cache coordinates
      return result;
    }
  }

  return null;
}

/**
 * Get distance and duration using Google Directions API via backend proxy
 * This avoids CORS issues by calling our backend endpoint instead of Google directly
 */
async function getDistanceWithGoogle(
  origin: LatLng,
  destination: LatLng,
): Promise<DistanceAndDuration | null> {
  try {
    const response = await fetch("/api/route-generator/distance", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ origin, destination }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn(
        `[DISTANCE] Distance API error: ${response.status}`,
        errorData
      );
      return null;
    }

    const result = await response.json();
    
    if (result.error) {
      console.warn(`[DISTANCE] Distance API error:`, result);
      return null;
    }

    const distanceResult: DistanceAndDuration = {
      distanceKm: result.distanceKm,
      durationMinutes: result.durationMinutes,
      method: result.method || "Google Directions API",
    };

    console.log(
      `[DISTANCE] Google Directions (via proxy): (${origin.lat.toFixed(4)}, ${origin.lng.toFixed(4)}) → (${destination.lat.toFixed(4)}, ${destination.lng.toFixed(4)}) = ${distanceResult.distanceKm} km, ${distanceResult.durationMinutes} min`,
    );
    return distanceResult;
  } catch (error) {
    console.error("[DISTANCE] Distance API error:", error);
    return null;
  }
}

/**
 * Get distance and duration between two coordinates
 * Uses cache, Google API, or Haversine fallback
 */
export async function getDistanceAndDuration(
  origin: LatLng,
  destination: LatLng,
): Promise<DistanceAndDuration> {
  const cacheKey = `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}:${destination.lat.toFixed(6)},${destination.lng.toFixed(6)}`;

  // Check cache
  const cached = distanceCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  // Try Google Directions API
  if (isGoogleMapsAvailable()) {
    const result = await getDistanceWithGoogle(origin, destination);
    if (result) {
      distanceCache.set(cacheKey, result);
      return result;
    }
  }

  // Fallback: Haversine
  const distanceKm = haversineDistance(origin, destination);
  const durationMinutes = estimateDurationFromDistance(distanceKm);

  const result: DistanceAndDuration = {
    distanceKm: Math.round(distanceKm * 10) / 10,
    durationMinutes,
    method: "Haversine (fallback)",
  };

  if (!isGoogleMapsAvailable()) {
    console.log(
      `[DISTANCE] Using Haversine fallback (no Google API key): (${origin.lat.toFixed(4)}, ${origin.lng.toFixed(4)}) → (${destination.lng.toFixed(4)}, ${destination.lng.toFixed(4)}) = ${result.distanceKm} km, ${result.durationMinutes} min (80 km/h assumed)`,
    );
  }

  distanceCache.set(cacheKey, result);
  return result;
}

/**
 * Simplified: Get distance and duration by location strings
 * Combines geocoding + distance calculation
 */
export async function getDistanceByLocation(
  originLocation: string,
  destinationLocation: string,
): Promise<DistanceAndDuration | null> {
  const originCoords = await getCoordinatesForLocation(originLocation);
  if (!originCoords) {
    console.error(`[DISTANCE] Failed to resolve origin: "${originLocation}"`);
    return null;
  }

  const destCoords = await getCoordinatesForLocation(destinationLocation);
  if (!destCoords) {
    console.error(
      `[DISTANCE] Failed to resolve destination: "${destinationLocation}"`,
    );
    return null;
  }

  return getDistanceAndDuration(originCoords, destCoords);
}

/**
 * Log initialization status
 */
export function logDistanceServiceStatus(): void {
  if (isGoogleMapsAvailable()) {
    console.log(
      "[DISTANCE] ✓ Google Maps API configured – using as primary source",
    );
  } else {
    console.warn(
      "[DISTANCE] ✗ VITE_GOOGLE_MAPS_API_KEY not configured – Haversine fallback only",
    );
    console.warn(
      "[DISTANCE] To enable Google Maps, set VITE_GOOGLE_MAPS_API_KEY in environment",
    );
    console.warn(
      "[DISTANCE] For local dev: add to .env.local file",
    );
    console.warn(
      "[DISTANCE] For Cloudflare Pages: set in Environment Variables (Settings → Environment Variables)",
    );
  }
}

/**
 * Clear caches (for testing)
 */
export function clearDistanceCaches(): void {
  coordinateCache.clear();
  distanceCache.clear();
  console.log("[DISTANCE] Caches cleared");
}
